<template>
  <div class="container">
    <Toolbar class="mb-3">
      <template #start>
        <h2>{{ $t('userappointmentspage.title') }}</h2>
      </template>
      <template #end>
        <Button
          class="p-button-rounded p-button-outlined mr-2"
          @click="createAppointment"
        >
          {{ $t('userappointmentspage.createappointment') }}
        </Button>
      </template>
    </Toolbar>

    <TabView :lazy="true">
      <TabPanel :header="$t('userappointmentspage.tab.upcoming')">
        <AppointmentListComponent
          :appointments="nextAppointments"
          @update="fetchNextAppointments"
        />
        <Paginator
          v-model:rows="nextAppointmentFilter.pageable.size"
          :total-records="totalNextAppointments"
          @page="onNextAppointmentsPageChange($event)"
        />
      </TabPanel>
      <TabPanel :header="$t('userappointmentspage.tab.all')">
        <AppointmentListComponent
          :appointments="appointments"
          @update="fetchAppointments"
        />

        <Paginator
          v-model:rows="appointmentFilter.pageable.size"
          :total-records="totalAppointments"
          @page="onAppointmentsPageChange($event)"
        />
      </TabPanel>
      <TabPanel
        :header="$t('userappointmentspage.tab.immediatetagging')"
      >
        <div v-if="immediateTaggings.length > 0">
          <template
            v-for="(day) in Object.keys(immediateTaggingsByDay).sort().reverse()"
            :key="day"
          >
            <h3>{{ toLocaleDateString(immediateTaggingsByDay[day][0].creationDate) }}</h3>
            <immediate-tagging-overview
              v-for="immediateTagging in immediateTaggingsByDay[day]"
              :key="immediateTagging.id"
              :immediate-tagging="immediateTagging"
            />
          </template>

          <Paginator
            v-model:rows="immediateTaggingFilter.pageable.size"
            :total-records="totalImmediateTaggings"
            @page="onImmediateTaggingsPageChange($event)"
          />
        </div>
        <div v-else>
          {{ $t('appointmentspage.noappointmentfound') }}
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<script>
import AppointmentListComponent from '@/components/appointments/AppointmentListComponent'
import {CONTACT_ROLE} from "@/shared/constants";
import Toolbar from "primevue/toolbar";
import Button from "primevue/button";
import TabPanel from "primevue/tabpanel";
import TabView from "primevue/tabview";
import ImmediateTaggingOverview from "@/components/immediatetagging/ImmediateTaggingOverview.vue";
import Paginator from "primevue/paginator";
import {convertDateStringToDayLocaleString} from "@/shared/utils/dateTimeUtils";
import {reactive} from "vue";
import useImmediateTaggingFilter from "@/composables/filters/useImmediateTaggingFilter";
import useAppointmentFilter from "@/composables/filters/useAppointmentFilter";
import useAppointmentStatus from "@/composables/useAppointmentStatus";

export default {
  name: 'UserAppointmentsView',
  components: {ImmediateTaggingOverview, AppointmentListComponent, Toolbar, Button, TabView, TabPanel, Paginator},
  setup() {
    const appointmentStatus = useAppointmentStatus();
    const immediateTaggingFilter = reactive(useImmediateTaggingFilter());

    const nextAppointmentFilter = reactive(useAppointmentFilter());
    nextAppointmentFilter.filters.validated.value = true;

    const appointmentFilter = reactive(useAppointmentFilter());
    appointmentFilter.filters.validated.value = true;

    return {
      appointmentStatus,
      immediateTaggingFilter,
      nextAppointmentFilter,
      appointmentFilter
    }
  },
  data() {
    return {
      appointments: [],
      totalAppointments: 0,

      nextAppointments: [],
      totalNextAppointments: 0,

      immediateTaggings: [],
      immediateTaggingsByDay: {},
      totalImmediateTaggings: 0,
      userId: null,
    }
  },
  computed: {
    isAgent() {
      return this.$store.state.auth.user.role !== CONTACT_ROLE;
    }
  },
  async mounted() {
    this.userId = this.$route.params.userId || null
    if (this.userId &&
      this.$store.state.auth.user.role === CONTACT_ROLE) {
      await this.$router.push('/')
    }

    this.nextAppointmentFilter.pageable.sortField = 'start';
    this.nextAppointmentFilter.pageable.sortOrder = -1;
    this.nextAppointmentFilter.pageable.size = 20;
    this.nextAppointmentFilter.filters.userId.value = this.userId ? this.userId : this.$store.state.auth.user.id;
    this.nextAppointmentFilter.filters.validated.value = true;
    await this.fetchNextAppointments();

    this.appointmentFilter.pageable.sortField = 'start';
    this.appointmentFilter.pageable.sortOrder = -1;
    this.appointmentFilter.pageable.size = 20;
    this.appointmentFilter.filters.userId.value = this.userId ? this.userId : this.$store.state.auth.user.id;
    this.appointmentFilter.filters.validated.value = true;
    await this.fetchAppointments();

    this.immediateTaggingFilter.pageable.sortField = 'creationDate';
    this.immediateTaggingFilter.pageable.sortOrder = -1;
    this.immediateTaggingFilter.pageable.size = 10;
    this.immediateTaggingFilter.filters.userId.value = this.userId ? this.userId : this.$store.state.auth.user.id;
    await this.fetchImmediateTaggings();
  },
  methods: {
    toLocaleDateString(date) {
      return convertDateStringToDayLocaleString(date, this.$i18n.locale)
    },

    async fetchAppointments() {
      const result = await this.$appointmentService.getAll(this.appointmentFilter.getQueryParams());
      this.appointments = result.content
      this.totalAppointments = result.totalElements;
    },
    async fetchNextAppointments() {
      this.nextAppointmentFilter.filters.start.value = new Date();
      const result = await this.$appointmentService.getAll(this.nextAppointmentFilter.getQueryParams());
      this.nextAppointments = result.content
      this.totalNextAppointments = result.totalElements;
    },
    async fetchImmediateTaggings() {
      const result = await this.$immediateTaggingService.getAll(this.immediateTaggingFilter.getQueryParams());
      this.immediateTaggings = result.content;
      this.totalImmediateTaggings = result.totalElements;

      for (const immediateTagging of this.immediateTaggings) {
        const date = new Date(immediateTagging.creationDate);
        date.setHours(0, 0, 0, 0);
        const key = date.getTime();

        if (!this.immediateTaggingsByDay[key]) {
          this.immediateTaggingsByDay[key] = [];
        }
        this.immediateTaggingsByDay[key].push(immediateTagging);
      }
    },

    onAppointmentsPageChange(event) {
      this.appointmentFilter.pageable.page = event.page;
      this.fetchAppointments();
    },
    onNextAppointmentsPageChange(event) {
      this.nextAppointmentFilter.pageable.page = event.page;
      this.fetchNextAppointments();
    },
    onImmediateTaggingsPageChange(event) {
      this.immediateTaggingFilter.pageable.page = event.page;
      this.fetchImmediateTaggings();
    },

    createAppointment() {
      if (this.userId) {
        this.$router.push({name: 'appointmentCreate', params: {userId: this.userId}})
      } else {
        this.$router.push({name: 'appointmentCreate'})
      }
    }
  }
}
</script>
